import { useEffect, useState } from 'react'

import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'

import { SALES_CHANNEL_COPY } from '#app/routes/mocra+/_form+/components/sales-channels/constants.ts'
import { SalesChannelForm } from '#app/routes/mocra+/_form+/components/sales-channels/form.tsx'
import {
	createSalesChannelDraft,
	sanitizeSalesChannelDraft,
	type SalesChannelDraft,
} from '#app/routes/mocra+/_form+/components/sales-channels/types.ts'

type SalesChannelOverlayMode = 'add' | 'edit'

type DraftErrors = Partial<Record<keyof SalesChannelDraft, string>>

type Props = {
	readonly mode: SalesChannelOverlayMode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: SalesChannelDraft) => void
	readonly initialValue?: SalesChannelDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function SalesChannelOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<SalesChannelDraft>(
		initialValue ? { ...initialValue } : createSalesChannelDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createSalesChannelDraft())
			setErrors({})
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open) {
			onClosed?.()
		}
	}, [onClosed, open])

	const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeSalesChannelDraft(draft)
		const nextErrors: DraftErrors = {}
		if (!sanitized.name) {
			nextErrors.name = 'Name the channel or partner.'
		}

		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors)
			return
		}

		onSubmit(sanitized)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	const titleCopy =
		mode === 'add'
			? SALES_CHANNEL_COPY.title.add
			: SALES_CHANNEL_COPY.title.edit
	const submitCopy =
		mode === 'add'
			? SALES_CHANNEL_COPY.submit.add
			: SALES_CHANNEL_COPY.submit.edit

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={SALES_CHANNEL_COPY.description}
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="sales-channel-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<SalesChannelForm
				formId="sales-channel-overlay-form"
				draft={draft}
				errors={errors}
				onDraftFieldChange={(field, value) =>
					setDraft((prev) => ({ ...prev, [field]: value }))
				}
				onSubmit={handleSubmit}
			/>
		</ResponsiveOverlay>
	)
}
